import { Panel } from '@hologram-dimension/panel';
import Field from 'components/Field/Field';
import PanelSection from 'components/PanelSection/PanelSection';
import { FC, useMemo } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { getUserFriendlyBytes } from 'utils/bytes';
import formatMoney from 'utils/numbers/formatMoney';
import { useUrl } from 'utils/hooks';
import { Link } from '@hologram-dimension/link';
import { LegacyPlan, LegacyPoolPlan } from 'types/legacyDashboard';
import styles from './PlanPricingPanel.module.css';

interface PlanPricingPanelProps {
  plan: LegacyPlan;
  planCoverageUrl: string;
  staticPool?: LegacyPoolPlan;
  simCount: number;
}

const PlanPricingPanel: FC<PlanPricingPanelProps> = ({
  plan,
  planCoverageUrl,
  staticPool,
  simCount,
}) => {
  const organizationId = useOrganization();
  const {
    perLineCharge,
    dataLimit,
    variablerate,
    overageCharge,
    outboundSms,
    zeroDataRate,
    id,
    zone,
  } = plan;
  const coverageUrl = useUrl({
    url: `${planCoverageUrl}`,
    replacers: [
      { placeholder: 'orgid', value: organizationId?.toString() ?? '' },
      { placeholder: 'dataPlanId', value: id.toString() },
      { placeholder: 'dataPlanZone', value: zone },
    ],
  });

  const includedData = useMemo(() => {
    if (staticPool) {
      return getUserFriendlyBytes(staticPool.data);
    }
    return dataLimit === 0 ? 'Pay as you go' : getUserFriendlyBytes(dataLimit);
  }, [staticPool, dataLimit]);

  return (
    <Panel header="SIM pricing" noContentPadding>
      <PanelSection
        variant="complex"
        heading="Data usage"
        headingIcon="CloudDataTransfer"
        fieldGroup={
          <>
            {staticPool && <Field label="Data pool type" value="Fixed size" />}
            <Field label="Included data" value={<strong>{includedData}</strong>} />
            {staticPool && <Field label="SIMs sharing data" value={`${simCount} SIMs`} />}
            <Field
              label={dataLimit > 0 || staticPool ? 'Overage per MB' : 'Usage per MB'}
              value={
                variablerate ? (
                  <Link href={coverageUrl!} type="inline">
                    Varies by network
                  </Link>
                ) : (
                  formatMoney(overageCharge)
                )
              }
            />
            <Field
              label="Data limits"
              value={<span className={styles.diminished}>Set per SIM</span>}
            />
          </>
        }
        notes={
          !variablerate && (
            <div className={styles.notes}>
              Session charges are aggregated until reaching a minimum charge of $0.25.
            </div>
          )
        }
      />
      <PanelSection
        variant="complex"
        heading="SMS"
        headingIcon="Chat"
        fieldGroup={
          <>
            <Field label="Per outbound SMS" value={formatMoney(outboundSms)} />
            <Field
              label="Per inbound SMS"
              value={<span className={styles.diminished}>Always free</span>}
            />
          </>
        }
      />
      <PanelSection
        variant="complex"
        heading="Monthly recurring charge"
        headingIcon="Receipt"
        fieldGroup={
          <>
            <Field label="Per SIM" value={formatMoney(perLineCharge)} />
            {zeroDataRate && <Field label="Zero-data rate" value={formatMoney(zeroDataRate)} />}
            {staticPool && (
              <Field
                label="Pool included data"
                value={formatMoney(parseFloat(staticPool.amount))}
              />
            )}
          </>
        }
        notes={
          zeroDataRate ? (
            <div className={styles.notes}>
              If your SIM sends no sessions within a billing period, it will be charged at the
              zero-data rate.
            </div>
          ) : null
        }
      />
    </Panel>
  );
};

export default PlanPricingPanel;
